import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { memo, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";

import MaskIcon from "@/components/Core/MaskIcon";
import { useDeviceType, useTranslate } from "@/hooks";
import { NavList } from "@/layout/styles";
import { CheckSidebar } from "@/types";

import SidebarSectionLabel from "../SidebarSectionLabel";
import NavItem from "./NavItem";
import { resolveSidebarLabel } from "./utils";

/** Where the folded groups are remembered between visits. */
const STORAGE_KEY = "skystake:sidebar-collapsed";

const readCollapsed = (): string[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(stored) ? stored.map(String) : [];
  } catch {
    return [];
  }
};

const writeCollapsed = (groups: string[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
  } catch {
    // Storage is off or full. The group still folds for this visit.
  }
};

/**
 * One group of the menu, under its heading.
 *
 * A group with a heading folds away on a press of it, and stays folded on the
 * next visit: the menu is as long as an admin makes it, and a player who never
 * opens a section had no way to get it out of the way of the ones they do.
 * The group holding the page the player is on is shown whatever was saved, so
 * the menu never hides where they are.
 */
function NavigationGroup({
  title,
  items,
  toggleDrawer,
}: {
  title?: string;
  items: CheckSidebar[];
  toggleDrawer?: () => void;
}) {
  const { isTablet } = useDeviceType();
  const translate = useTranslate();
  const { pathname } = useLocation();

  const [collapsed, setCollapsed] = useState<boolean>(
    () => !!title && readCollapsed().includes(title),
  );

  const toggle = useCallback(() => {
    if (!title) return;

    setCollapsed((previous) => {
      const next = !previous;
      const others = readCollapsed().filter((group) => group !== title);
      writeCollapsed(next ? [...others, title] : others);
      return next;
    });
  }, [title]);

  const holdsCurrentPage = items.some((item) => item.url === pathname);
  // The strip of icons a tablet shows has no headings to fold by.
  const foldable = !isTablet && !!title;
  const shown = !foldable || !collapsed || holdsCurrentPage;
  const listId = title ? `${title}-items` : undefined;

  return (
    <Box component="nav" aria-label={title || "menu"}>
      {foldable && (
        <Box sx={{ pl: 1, pt: 1 }}>
          <SidebarSectionLabel
            id={`${title}-heading`}
            expanded={shown}
            controls={listId}
            onToggle={toggle}
          >
            {resolveSidebarLabel(translate, title)}
          </SidebarSectionLabel>
        </Box>
      )}

      {/* The <ul> sits inside the Collapse so its only children are <li>s. */}
      <Collapse in={shown} id={listId} unmountOnExit>
        <NavList
          dense
          isTablet={isTablet}
          aria-labelledby={title ? `${title}-heading` : undefined}
          sx={foldable ? { pt: 0 } : undefined}
        >
          {items.map((item) => (
            <NavItem
              key={item._id}
              url={item.url}
              onClick={toggleDrawer}
              icon={
                item.image ? <MaskIcon className="nav-icon" src={item.image} size={20} /> : null
              }
              label={resolveSidebarLabel(translate, item.name)}
            />
          ))}
        </NavList>
      </Collapse>
    </Box>
  );
}

export default memo(NavigationGroup);
